import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useUserContext } from './UserContext';

type Frame = Record<string, any>;
type FrameHandler = (frame: Frame) => void;

interface ClientSocketContextValue {
  /** Send a frame if connected; returns false when the socket isn't open */
  send: (frame: Frame) => boolean;
  /** Register an incoming-frame handler; returns an unsubscribe fn */
  subscribe: (handler: FrameHandler) => () => void;
  connected: boolean;
  /**
   * Increments on every successful (re)connect. Consumers watch it to
   * re-open their channels after a drop.
   */
  epoch: number;
}

const ClientSocketContext = createContext<ClientSocketContextValue>({
  send: () => false,
  subscribe: () => () => {},
  connected: false,
  epoch: 0,
});

export const useClientSocket = () => useContext(ClientSocketContext);

/**
 * Owns the single multiplexed client WebSocket (/api/ws/client). All chat
 * conversations, presence, and the activity/bell summary ride this one
 * connection as tagged frames; consumers (useTeamChatSocket,
 * ChatActivityContext) send framed messages and filter incoming frames.
 * Handles reconnect with backoff and a keepalive ping.
 */
export const ClientSocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isLoggedIn } = useUserContext();
  const [connected, setConnected] = useState(false);
  const [epoch, setEpoch] = useState(0);

  const wsRef = useRef<WebSocket | null>(null);
  const handlersRef = useRef<Set<FrameHandler>>(new Set());
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const attemptsRef = useRef(0);
  const closedRef = useRef(false);

  useEffect(() => {
    if (!isLoggedIn) {
      setConnected(false);
      return;
    }
    closedRef.current = false;

    const clearTimers = () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    };

    const connect = () => {
      if (closedRef.current) return;
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new WebSocket(
        `${protocol}//${window.location.host}/api/ws/client`,
      );
      wsRef.current = ws;

      ws.onopen = () => {
        attemptsRef.current = 0;
        setConnected(true);
        setEpoch((e) => e + 1);
        heartbeatRef.current = setInterval(() => {
          if (ws.readyState === ws.OPEN)
            ws.send(JSON.stringify({ op: 'ping' }));
        }, 25_000);
      };
      ws.onmessage = (message) => {
        let frame: Frame;
        try {
          frame = JSON.parse(message.data);
        } catch {
          return;
        }
        if (frame.op === 'pong') return;
        for (const handler of handlersRef.current) {
          try {
            handler(frame);
          } catch (error) {
            console.error('client-socket handler error:', error);
          }
        }
      };
      ws.onclose = () => {
        clearTimers();
        setConnected(false);
        if (closedRef.current) return;
        const delay = Math.min(1000 * 2 ** attemptsRef.current, 30_000);
        attemptsRef.current += 1;
        reconnectTimerRef.current = setTimeout(connect, delay);
      };
      ws.onerror = () => ws.close();
    };

    connect();

    return () => {
      closedRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      clearTimers();
      wsRef.current?.close();
      wsRef.current = null;
      setConnected(false);
    };
  }, [isLoggedIn]);

  const send = useCallback((frame: Frame): boolean => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    ws.send(JSON.stringify(frame));
    return true;
  }, []);

  const subscribe = useCallback((handler: FrameHandler) => {
    handlersRef.current.add(handler);
    return () => {
      handlersRef.current.delete(handler);
    };
  }, []);

  return (
    <ClientSocketContext.Provider value={{ send, subscribe, connected, epoch }}>
      {children}
    </ClientSocketContext.Provider>
  );
};
